import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { Children as $iqq3r$Children, cloneElement as $iqq3r$cloneElement, useState as $iqq3r$useState, useRef as $iqq3r$useRef, useEffect as $iqq3r$useEffect, useCallback as $iqq3r$useCallback, useReducer as $iqq3r$useReducer } from "react";
import { flushSync as $iqq3r$flushSync } from "react-dom";
import { useComposedRefs as $iqq3r$useComposedRefs } from "@radix-ui/react-compose-refs";
import { useLayoutEffect as $iqq3r$useLayoutEffect } from "@radix-ui/react-use-layout-effect";

function $fe963b355347cc68$export$3e6543de14f8614f(initialState, machine) {
  return $iqq3r$useReducer(function (state, event) {
    var nextState = machine[state][event];
    return nextState !== null && nextState !== void 0 ? nextState : state;
  }, initialState);
}

var $921a889cee6df7e8$export$99c2b779aa4e8b8b = function $921a889cee6df7e8$export$99c2b779aa4e8b8b(props) {
  var present = props.present,
      children = props.children;
  var presence = $921a889cee6df7e8$var$usePresence(present);
  var child = typeof children === 'function' ? children({
    present: presence.isPresent
  }) : $iqq3r$Children.only(children);
  var ref = $iqq3r$useComposedRefs(presence.ref, child.ref);
  var forceMount = typeof children === 'function';
  return forceMount || presence.isPresent ? /*#__PURE__*/$iqq3r$cloneElement(child, {
    ref: ref
  }) : null;
};

$921a889cee6df7e8$export$99c2b779aa4e8b8b.displayName = 'Presence';
/* -------------------------------------------------------------------------------------------------
 * usePresence
 * -----------------------------------------------------------------------------------------------*/

function $921a889cee6df7e8$var$usePresence(present) {
  var _$iqq3r$useState = $iqq3r$useState(),
      _$iqq3r$useState2 = _slicedToArray(_$iqq3r$useState, 2),
      node1 = _$iqq3r$useState2[0],
      setNode = _$iqq3r$useState2[1];

  var stylesRef = $iqq3r$useRef({});
  var prevPresentRef = $iqq3r$useRef(present);
  var prevAnimationNameRef = $iqq3r$useRef('none');
  var initialState = present ? 'mounted' : 'unmounted';

  var _$fe963b355347cc68$ex = $fe963b355347cc68$export$3e6543de14f8614f(initialState, {
    mounted: {
      UNMOUNT: 'unmounted',
      ANIMATION_OUT: 'unmountSuspended'
    },
    unmountSuspended: {
      MOUNT: 'mounted',
      ANIMATION_END: 'unmounted'
    },
    unmounted: {
      MOUNT: 'mounted'
    }
  }),
      _$fe963b355347cc68$ex2 = _slicedToArray(_$fe963b355347cc68$ex, 2),
      state = _$fe963b355347cc68$ex2[0],
      send = _$fe963b355347cc68$ex2[1];

  $iqq3r$useEffect(function () {
    var currentAnimationName = $921a889cee6df7e8$var$getAnimationName(stylesRef.current);
    prevAnimationNameRef.current = state === 'mounted' ? currentAnimationName : 'none';
  }, [state]);
  $iqq3r$useLayoutEffect(function () {
    var styles = stylesRef.current;
    var wasPresent = prevPresentRef.current;
    var hasPresentChanged = wasPresent !== present;

    if (hasPresentChanged) {
      var prevAnimationName = prevAnimationNameRef.current;
      var currentAnimationName = $921a889cee6df7e8$var$getAnimationName(styles);
      if (present) send('MOUNT');else if (currentAnimationName === 'none' || (styles === null || styles === void 0 ? void 0 : styles.display) === 'none') // If there is no exit animation or the element is hidden, animations won't run
        // so we unmount instantly
        send('UNMOUNT');else {
        /**
        * When `present` changes to `false`, we check changes to animation-name to
        * determine whether an animation has started. We chose this approach (reading
        * computed styles) because there is no `animationrun` event and `animationstart`
        * fires after `animation-delay` has expired which would be too late.
        */
        var isAnimating = prevAnimationName !== currentAnimationName;
        if (wasPresent && isAnimating) send('ANIMATION_OUT');else send('UNMOUNT');
      }
      prevPresentRef.current = present;
    }
  }, [present, send]);
  $iqq3r$useLayoutEffect(function () {
    if (node1) {
      /**
      * Triggering an ANIMATION_OUT during an ANIMATION_IN will fire an `animationcancel`
      * event for ANIMATION_IN after we have entered `unmountSuspended` state. So, we
      * make sure we only trigger ANIMATION_END for the currently active animation.
      */
      var handleAnimationEnd = function handleAnimationEnd(event) {
        var currentAnimationName = $921a889cee6df7e8$var$getAnimationName(stylesRef.current);
        var isCurrentAnimation = currentAnimationName.includes(event.animationName);
        if (event.target === node1 && isCurrentAnimation) // With React 18 concurrency this update is applied
          // a frame after the animation ends, creating a flash of visible content.
          // By manually flushing we ensure they sync within a frame, removing the flash.
          $iqq3r$flushSync(function () {
            return send('ANIMATION_END');
          });
      };

      var handleAnimationStart = function handleAnimationStart(event) {
        if (event.target === node1) // if animation occurred, store its name as the previous animation.
          prevAnimationNameRef.current = $921a889cee6df7e8$var$getAnimationName(stylesRef.current);
      };

      node1.addEventListener('animationstart', handleAnimationStart);
      node1.addEventListener('animationcancel', handleAnimationEnd);
      node1.addEventListener('animationend', handleAnimationEnd);
      return function () {
        node1.removeEventListener('animationstart', handleAnimationStart);
        node1.removeEventListener('animationcancel', handleAnimationEnd);
        node1.removeEventListener('animationend', handleAnimationEnd);
      };
    } else // Transition to the unmounted state if the node is removed prematurely.
      // We avoid doing so during cleanup as the node may change but still exist.
      send('ANIMATION_END');
  }, [node1, send]);
  return {
    isPresent: ['mounted', 'unmountSuspended'].includes(state),
    ref: $iqq3r$useCallback(function (node) {
      if (node) stylesRef.current = getComputedStyle(node);
      setNode(node);
    }, [])
  };
}
/* -----------------------------------------------------------------------------------------------*/


function $921a889cee6df7e8$var$getAnimationName(styles) {
  return (styles === null || styles === void 0 ? void 0 : styles.animationName) || 'none';
}

export { $921a889cee6df7e8$export$99c2b779aa4e8b8b as Presence };